import * as React from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { Switch } from "@mui/material";
import FormControl from "@mui/material/FormControl";
import Grid from "@mui/material/Grid";
import InputLabel from "@mui/material/InputLabel";
import OutlinedInput from "@mui/material/OutlinedInput";
import { EyeIcon } from "@phosphor-icons/react/dist/ssr/Eye";
import { EyeSlashIcon } from "@phosphor-icons/react/dist/ssr/EyeSlash";
import { Controller, useForm } from "react-hook-form";
import { z as zod } from "zod";

import { useCreateUser, useUpdateUser, type User } from "@/hooks/api/use-users";
import { CustomDrawer } from "@/components/custom-drawer";
import { CustomFormHelperError } from "@/components/customFormHelper/custom-form-helper-error";

export interface CreateUserProps {
	user: User | null | undefined;
	isCreateDrawerOpen: boolean;
	onCreateDrawerClose: () => void;
}

const schema = zod.object({
	email: zod.string().email("Must be a valid email").min(1, "Email is required"),
	name: zod.string().min(1, "Name is required"),
	password: zod.string().optional(),
	isSuperAdmin: zod.boolean(),
});

type Values = zod.infer<typeof schema>;

export function CreateUser({
	user,
	isCreateDrawerOpen,
	onCreateDrawerClose,
}: CreateUserProps): React.JSX.Element {
	const [isPasswordVisible, setIsPasswordVisible] = React.useState(false);
	const isEdit = !!user;

	const createUserMutation = useCreateUser();
	const updateUserMutation = useUpdateUser();

	const defaultValues = {
		email: user?.email || "",
		name: user?.name || "",
		password: "",
		isSuperAdmin: user?.isSuperAdmin || false,
	} satisfies Values;

	const {
		control,
		handleSubmit,
		formState: { errors, isSubmitting },
		reset,
		setError,
	} = useForm<Values>({
		defaultValues,
		resolver: zodResolver(schema),
	});

	React.useLayoutEffect(() => {
		reset({
			email: user?.email || "",
			name: user?.name || "",
			password: "",
			isSuperAdmin: user?.isSuperAdmin || false,
		});
	}, [reset, user, isCreateDrawerOpen]);

	const onSubmitForm = async (data: Values) => {
		try {
			await (isEdit && user?.id
				? updateUserMutation.mutateAsync({ id: user.id, data })
				: createUserMutation.mutateAsync(data));

			onCreateDrawerClose();
		} catch (error: any) {
			const errorMessages = error?.response?.data?.message;

			if (!Array.isArray(errorMessages)) {
				setError("root", { message: errorMessages });
				return;
			}

			const errorMessage = errorMessages[0];
			setError(errorMessage.split(" ")[0], { message: errorMessage });
		}
	};
	return (
		<CustomDrawer
			open={isCreateDrawerOpen}
			onClose={onCreateDrawerClose}
			title={isEdit ? "Edit User" : "Create User"}
			onSubmit={handleSubmit(onSubmitForm)}
			isSubmitButtonDisabled={isSubmitting || createUserMutation.isPending || updateUserMutation.isPending}
			submitButtonText={isEdit ? "Update User" : "Create User"}
      width="420px"
		>
			<form onSubmit={handleSubmit(onSubmitForm)}>
				<Grid container spacing={2}>
					<Grid
						size={{
							md: 12,
							xs: 12,
						}}
					>
						<Controller
							control={control}
							name="name"
							render={({ field }) => (
								<FormControl error={Boolean(errors.name)} fullWidth>
									<InputLabel>Name</InputLabel>
									<OutlinedInput {...field} placeholder="Enter name" />
									{errors.name ? <CustomFormHelperError text={errors.name.message || ""} /> : null}
								</FormControl>
							)}
						/>
					</Grid>
					<Grid
						size={{
							md: 12,
							xs: 12,
						}}
					>
						<Controller
							control={control}
							name="email"
							render={({ field }) => (
								<FormControl error={Boolean(errors.email)} fullWidth>
									<InputLabel>Email address</InputLabel>
									<OutlinedInput {...field} type="email" placeholder="Enter email address" />
									{errors.email ? <CustomFormHelperError text={errors.email.message || ""} /> : null}
								</FormControl>
							)}
						/>
					</Grid>
					{!isEdit && (
						<Grid
							size={{
								md: 12,
								xs: 12,
							}}
						>
							<Controller
								control={control}
								name="password"
								render={({ field }) => (
									<FormControl error={Boolean(errors.password)} fullWidth>
										<InputLabel>Password</InputLabel>
										<OutlinedInput
											{...field}
											placeholder="Enter password"
											endAdornment={
												isPasswordVisible ? (
													<EyeIcon
														cursor="pointer"
														fontSize="var(--icon-fontSize-md)"
														onClick={(): void => {
															setIsPasswordVisible(false);
														}}
													/>
												) : (
													<EyeSlashIcon
														cursor="pointer"
														fontSize="var(--icon-fontSize-md)"
														onClick={(): void => {
															setIsPasswordVisible(true);
														}}
													/>
												)
											}
											type={isPasswordVisible ? "text" : "password"}
										/>
										{errors.password ? <CustomFormHelperError text={errors.password.message || ""} /> : null}
									</FormControl>
								)}
							/>
						</Grid>
					)}
					<Grid
						size={{
							md: 12,
							xs: 12,
						}}
					>
						<Controller
							control={control}
							name="isSuperAdmin"
							render={({ field }) => (
								<FormControl error={Boolean(errors.isSuperAdmin)} fullWidth>
									<InputLabel>Is Super Admin</InputLabel>
									<Switch {...field} type="checkbox" defaultChecked={field.value} />
									{errors.isSuperAdmin ? <CustomFormHelperError text={errors.isSuperAdmin.message || ""} /> : null}
								</FormControl>
							)}
						/>
					</Grid>
				</Grid>
			</form>
		</CustomDrawer>
	);
}
